import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LayoutGrid, Users, CreditCard, TriangleAlert, RefreshCw, Loader2, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BillingDrawer from "@/components/admin/BillingDrawer";
import GrantModal from "@/components/admin/GrantModal";
import AdminDashboard from "@/components/admin/AdminDashboard";
import AdminSubscribers from "@/components/admin/AdminSubscribers";
import AdminPayments from "@/components/admin/AdminPayments";
import AdminProblems from "@/components/admin/AdminProblems";

const TABS = [
  { key: "dashboard",   label: "Dashboard", icon: LayoutGrid,  title: "Admin Dashboard", subtitle: "Controllo abbonamenti, pagamenti e salute del sistema." },
  { key: "subscribers", label: "Abbonati",  icon: Users,       title: "Abbonati",        subtitle: "Gestione utenti, piani e stato abbonamenti." },
  { key: "payments",    label: "Pagamenti", icon: CreditCard,  title: "Pagamenti",       subtitle: "Storico incassi, rinnovi e pagamenti falliti." },
  { key: "problems",    label: "Problemi",  icon: TriangleAlert, title: "Problemi / Controllo", subtitle: "Anomalie operative, email, webhook e sistemi." },
];
const SUB_KEYS = ["q", "plan", "cycle", "origin", "billing_status"];
const PAY_KEYS = ["q", "status", "plan", "cycle"];

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allowed, setAllowed] = useState(null);
  const [period, setPeriod] = useState(30);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  // v9.50 Sprint 3 — drawer + grant popup state (kept).
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState("");
  const [grantModal, setGrantModal] = useState(null);
  const [busyId, setBusyId] = useState("");

  const tab = TABS.some((t) => t.key === searchParams.get("tab")) ? searchParams.get("tab") : "dashboard";
  const current = TABS.find((t) => t.key === tab);

  useEffect(() => {
    api.get("/admin/me")
      .then((r) => {
        const ok = !!r.data?.is_admin_owner;
        setAllowed(ok);
        if (!ok) { toast.error("Accesso non autorizzato"); navigate("/app/dashboard", { replace: true }); }
      })
      .catch(() => { setAllowed(false); navigate("/app/dashboard", { replace: true }); });
  }, [navigate]);

  const goTo = (nextTab, params = {}) => {
    const sp = new URLSearchParams();
    sp.set("tab", nextTab);
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "" && v !== "all") sp.set(k, String(v)); });
    setSearchParams(sp);
  };

  const subFilters = useMemo(() => {
    const f = {};
    SUB_KEYS.forEach((k) => { const v = searchParams.get(k); if (v) f[k] = v; });
    return f;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);
  const payFilters = useMemo(() => {
    const f = {};
    PAY_KEYS.forEach((k) => { const v = searchParams.get(k); if (v) f[k] = v; });
    return f;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const refreshAll = async () => {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
    setTimeout(() => setRefreshing(false), 600);
  };

  const openDrawer = async (uid) => {
    setSelectedUserId(uid);
    setOverview(null); setOverviewError(""); setOverviewLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${uid}/billing-overview`);
      setOverview(data);
    } catch (err) {
      setOverviewError(err.response?.data?.detail || err.message || "errore caricamento");
    } finally {
      setOverviewLoading(false);
    }
  };
  const closeDrawer = () => { setSelectedUserId(null); setOverview(null); setGrantModal(null); };

  const openGrantModal = () => {
    setGrantModal({ plan: "basic", duration: "one_month", grant_kind: "gift", reason: "", stripeWarn: null, ack: false });
  };

  // Grant flows EXCLUSIVELY through POST /admin/users/{id}/plan-grant.
  const submitGrant = async () => {
    if (!grantModal || !selectedUserId) return;
    try {
      const payload = {
        plan: grantModal.plan,
        duration: grantModal.duration,
        grant_kind: grantModal.grant_kind,
        reason: grantModal.reason || undefined,
        acknowledge_stripe_warning: !!grantModal.ack,
      };
      await api.post(`/admin/users/${selectedUserId}/plan-grant`, payload);
      toast.success(`Piano ${grantModal.plan.toUpperCase()} attivato`);
      setGrantModal(null);
      await openDrawer(selectedUserId);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === "stripe_subscription_active") {
        setGrantModal((m) => ({ ...m, stripeWarn: d, ack: false }));
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || err.message));
      }
    }
  };

  const retryBillingEmail = async (kind, invoiceId) => {
    if (!invoiceId) return;
    try {
      const { data } = await api.post(`/admin/billing-emails/${kind}/${invoiceId}/retry`);
      if (data.status === "sent") toast.success("Email inviata correttamente");
      else toast.warning(`Retry fallito: ${data.error || "errore"}`);
      if (selectedUserId) await openDrawer(selectedUserId);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const contractsBonus = async (uid) => {
    setBusyId(uid + ":bonus");
    try {
      await api.post(`/admin/users/${uid}/contracts-bonus`);
      toast.success("+10 contratti aggiunti");
      await openDrawer(uid);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusyId(""); }
  };
  const toggleSuspend = async (uid, suspended) => {
    setBusyId(uid + ":suspend");
    try {
      await api.post(`/admin/users/${uid}/${suspended ? "reactivate" : "suspend"}`);
      toast.success(suspended ? "Account riattivato" : "Account sospeso");
      await openDrawer(uid);
      setRefreshKey((k) => k + 1);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusyId(""); }
  };

  if (allowed === false) return null;
  if (allowed === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-7 h-7 text-primary animate-spin" strokeWidth={1.5} />
      </div>
    );
  }

  const showPeriod = tab !== "subscribers";

  return (
    <div className="space-y-6 py-2" data-testid="admin-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="label-uppercase text-primary">Admin</span>
          <h1 className="font-serif-heading text-4xl sm:text-5xl mt-1" data-testid="admin-title">{current.title}</h1>
          <p className="text-sm text-neutral-500 mt-1">{current.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {showPeriod && (
            <Select value={String(period)} onValueChange={(v) => setPeriod(Number(v))}>
              <SelectTrigger className="h-10 w-44 rounded-md bg-white" data-testid="admin-period-select">
                <CalendarDays className="w-4 h-4 mr-2 text-neutral-500" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Ultimi 7 giorni</SelectItem>
                <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                <SelectItem value="90">Ultimi 90 giorni</SelectItem>
                <SelectItem value="365">Ultimi 12 mesi</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" className="rounded-md h-10" onClick={refreshAll} disabled={refreshing} data-testid="admin-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} /> Aggiorna
          </Button>
        </div>
      </div>

      <nav className="border-b border-border flex items-center gap-1 overflow-x-auto" data-testid="admin-tabs">
        {TABS.map((t) => {
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => goTo(t.key)}
              className={`inline-flex items-center gap-2 px-4 h-11 text-sm border-b-2 -mb-px whitespace-nowrap transition-colors ${active ? "border-primary text-neutral-900 bg-primary/5 font-medium" : "border-transparent text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50"}`}
              data-testid={`admin-tab-${t.key}`}
              aria-current={active ? "page" : undefined}
            >
              <t.icon className={`w-4 h-4 ${active ? "text-primary" : ""}`} strokeWidth={1.7} /> {t.label}
            </button>
          );
        })}
      </nav>

      {tab === "dashboard" && <AdminDashboard period={period} goTo={goTo} onOpenUser={openDrawer} refreshKey={refreshKey} />}
      {tab === "subscribers" && <AdminSubscribers initialFilters={subFilters} onOpenUser={openDrawer} refreshKey={refreshKey} currentUserEmail={user?.email} />}
      {tab === "payments" && <AdminPayments period={period} initialFilters={payFilters} onOpenUser={openDrawer} refreshKey={refreshKey} />}
      {tab === "problems" && <AdminProblems period={period} focus={searchParams.get("focus")} goTo={goTo} onOpenUser={openDrawer} onRetryEmail={retryBillingEmail} refreshKey={refreshKey} />}

      {selectedUserId && (
        <BillingDrawer
          overview={overview}
          loading={overviewLoading}
          error={overviewError}
          onClose={closeDrawer}
          onGrantClick={openGrantModal}
          onRetryEmail={retryBillingEmail}
          onBonus={contractsBonus}
          onToggleSuspend={toggleSuspend}
          isOwner={overview?.user?.email?.toLowerCase() === user?.email?.toLowerCase()}
          busy={busyId}
        />
      )}
      {grantModal && (
        <GrantModal state={grantModal} setState={setGrantModal} onSubmit={submitGrant} onClose={() => setGrantModal(null)} />
      )}
    </div>
  );
}
